import {
  Ban,
  ChevronDown,
  Copy,
  Edit2,
  Eye,
  LogIn,
  MoreVertical,
  Plus,
  QrCode,
  RefreshCw,
  RotateCcw,
  Save,
  Send,
  Settings2,
  ShieldCheck,
  ToggleRight,
  Trash2,
  UserRound,
} from "lucide-react";
import type { DetailedHelpMap } from "./types";

/**
 * Ajuda detalhada: as 11 rotas /administracao/* (visíveis só para super_admin
 * "Allmight" — administração central da plataforma, não da propriedade).
 * Conteúdo verificado lendo cada page component real antes de escrever; onde
 * a regra mora no backend (checklist de implantação, cálculo da inspeção,
 * pré-requisitos do WhatsApp) o controller/service foi lido também.
 */
export const ADMINISTRACAO_HELP: DetailedHelpMap = {
  "/administracao/configuracoes-da-plataforma": [
    {
      title: "O que esta tela faz",
      description:
        "É o painel de controle da InnSight como empresa, não de um hotel. Duas coisas moram aqui: como a aplicação inteira está plugada na infraestrutura (banco, cache, fila, busca, armazenamento, e-mail) e quanto a InnSight retém de cada cobrança que passa pelo Asaas. Mexer aqui afeta todas as propriedades ao mesmo tempo — cada alteração fica registrada na auditoria administrativa com autor e horário.",
    },
    {
      title: "Ambiente e infraestrutura",
      description:
        "As variáveis de ambiente da aplicação, agrupadas por responsabilidade na coluna da esquerda (o número ao lado de cada grupo é quantas variáveis ele tem). O selo no canto direito diz se o que está na tela já é o que está rodando: \"Ambiente sincronizado\" significa que sim; \"Alteração aguardando aplicação\" significa que alguém salvou algo que o servidor ainda não aplicou.",
      items: [
        {
          label: "Campo de credencial (senha/token)",
          explanation:
            "O valor atual nunca volta para o navegador — o campo só diz \"Credencial armazenada e mascarada\" ou \"Credencial pendente\". Digitar algo substitui a credencial em uso; deixar em branco mantém a que já está lá. Não dá para conferir a senha atual digitando por cima: se errar, você troca sem saber o que tinha antes.",
        },
        {
          label: "Copiar credencial",
          domKey: "admin.platform.env_secret_copy",
          visual: { icon: Copy, text: "Copiar", tone: "outline" },
          explanation:
            "Só aparece em credenciais já configuradas. Manda o valor real direto para a área de transferência sem exibi-lo na tela — é como você recupera uma senha para colar em outro lugar sem que ela fique visível para quem está olhando o monitor.",
        },
        {
          label: "Aviso \"Em execução\"",
          explanation:
            "Aparece embaixo de um campo assim que você o altera, mostrando o valor que ainda está valendo no servidor. Serve para você comparar o que está prestes a salvar com o que está no ar agora.",
        },
        {
          label: "Salvar ambiente",
          domKey: "admin.platform.env_save",
          visual: { icon: Save, text: "Salvar ambiente", tone: "primary" },
          explanation:
            "Não aplica na hora. Grava a alteração como pendente e o agente de implantação (watchdog) aplica no próximo ciclo dele — por isso o sistema continua se comportando do jeito antigo por alguns instantes depois de salvar. Se o comportamento não mudou, o normal é esperar o ciclo, não salvar de novo.",
        },
      ],
    },
    {
      title: "Repasse global InnSight",
      description:
        "Quanto a InnSight fica de cada cobrança processada pelo Asaas. Vale como padrão para toda propriedade que não tenha uma exceção própria ativa.",
      items: [
        {
          label: "Percentual InnSight sobre a cobrança (%)",
          domKey: "admin.platform.split_percentage",
          explanation:
            "Fatia percentual retida em cada cobrança. É cumulativo com o valor fixo abaixo: os dois são aplicados na mesma transação, não é um ou outro.",
        },
        {
          label: "Valor fixo InnSight por cobrança (R$)",
          domKey: "admin.platform.split_fixed_fee",
          explanation:
            "Taxa fixa somada ao percentual, cobrada uma vez por transação. É o que garante um piso de receita em cobranças de valor baixo, onde só o percentual não pagaria o custo.",
        },
        {
          label: "Sua carteira Asaas destinatária (ID)",
          domKey: "admin.platform.split_wallet",
          explanation:
            "Wallet ID da InnSight que recebe o valor retido no split. Se estiver errado ou vazio, o split não tem para onde mandar o dinheiro — o valor fica com a propriedade.",
        },
        {
          label: "Salvar repasse global",
          domKey: "admin.platform.global_split_save",
          visual: { icon: Save, text: "Salvar repasse global", tone: "primary" },
          explanation:
            "Grava só os três campos deste bloco. Não mexe nas exceções por propriedade nem nas variáveis de ambiente — cada bloco desta tela tem o seu próprio salvar.",
          fact: (ctx) =>
            ctx.payments.asaas_environment === "sandbox"
              ? "o Asaas está em homologação (sandbox): os splits configurados aqui não movimentam dinheiro real."
              : "o Asaas está em produção: os splits configurados aqui valem sobre cobranças reais.",
        },
      ],
    },
    {
      title: "Exceções do repasse InnSight",
      description:
        "Uma linha por propriedade que tem política própria de repasse. A tabela mostra o percentual, o fixo e se a exceção está ativa. Quando não há nenhuma linha, todas as propriedades estão usando os parâmetros globais.",
      items: [
        {
          label: "Nova regra",
          domKey: "admin.platform.new_policy",
          visual: { icon: Plus, text: "Nova regra", tone: "outline" },
          explanation:
            "Abre o diálogo já preenchido com os valores globais como ponto de partida — você escolhe a propriedade e ajusta só o que muda para ela. Enquanto a exceção estiver ativa, os valores globais deixam de valer para aquela propriedade.",
        },
        {
          label: "Editar exceção da linha",
          domKey: "admin.platform.edit_policy",
          visual: { icon: Edit2, text: "Editar", tone: "outline" },
          explanation:
            "Abre a mesma regra para ajuste. Com a exceção já criada, a propriedade fica travada no diálogo — para mover uma regra de hotel você cria outra, não troca o dono desta.",
        },
        {
          label: "Habilitar exceção",
          visual: { icon: ToggleRight, text: "Habilitar exceção", tone: "outline" },
          explanation:
            "Desligar não apaga a regra: a propriedade volta a usar as taxas globais e o histórico da exceção continua guardado para religar depois. É o jeito de suspender um acordo comercial temporariamente sem perder os números combinados.",
        },
      ],
    },
  ],
  "/administracao/telemetria": [
    {
      title: "O que esta tela faz",
      description:
        "Raio-x da infraestrutura em tempo real, só para leitura: banco de dados, Redis, busca (Meilisearch), filas, memória, disco, sessões ativas, capacidade (CPU e conexões de banco) e a topologia das VMs distribuídas. É onde você olha primeiro quando um hotel reclama de lentidão ou de coisa que não salva — antes de procurar o erro na tela do cliente.",
      items: [
        {
          label: "Selo \"Plataforma operacional\" / \"Atenção necessária\"",
          explanation:
            "Resumo de todos os serviços num só indicador. \"Atenção necessária\" quer dizer que pelo menos um componente respondeu fora do esperado — os cards abaixo mostram qual.",
        },
        {
          label: "Cards de serviço (bolinha verde/vermelha)",
          explanation:
            "Cada card é um serviço da infraestrutura. Verde é respondendo, vermelho é fora do ar, cinza é não aplicável nesta instalação (serviço que não está em uso). O texto pequeno traz o detalhe técnico da checagem.",
        },
        {
          label: "Atualizar",
          visual: { icon: RefreshCw, text: "Atualizar", tone: "outline" },
          explanation:
            "Refaz a consulta aos serviços agora. Os números são de uma checagem pontual, não um monitoramento contínuo — durante um incidente vale clicar de novo em vez de confiar no que está na tela há minutos.",
        },
        {
          label: "Por que não há nada para configurar aqui",
          explanation:
            "Esta tela só observa. Qualquer mudança de infraestrutura (host, porta, credencial, driver) é feita em \"Configurações da plataforma\", na seção Ambiente e infraestrutura.",
        },
      ],
    },
  ],
  "/administracao/propriedades": [
    {
      title: "O que esta tela faz",
      description:
        "A lista mestre de todos os hotéis da plataforma. Cada linha junta em um lugar só o que costuma estar espalhado: quem é o titular, se ele já trocou a senha inicial, qual plano assinou e como está a cobrança, em que ponto da implantação a propriedade está e se ela está ativa ou suspensa. É daqui que você entra na conta de um cliente para dar suporte.",
      items: [
        {
          label: "Buscar propriedade",
          domKey: "admin.tenants.search",
          explanation:
            "Procura por nome, slug, CNPJ ou e-mail. A busca acontece no servidor com um pequeno atraso depois que você para de digitar. A tabela carrega até 50 propriedades de uma vez e não tem paginação — com a base maior que isso, use a busca para chegar na propriedade em vez de rolar a lista.",
        },
        {
          label: "Nova propriedade",
          domKey: "admin.tenants.new",
          visual: { icon: Plus, text: "Nova propriedade", tone: "primary" },
          explanation:
            "Abre o assistente de criação (nome e titular inicial) e, no fim, entrega uma senha temporária para entregar ao cliente — essa senha aparece uma vez só. O plano NÃO é escolhido aqui: ele é definido depois em \"Gerenciar\", e enquanto não estiver definido a implantação fica travada no passo do plano.",
        },
        {
          label: "Coluna \"Acesso inicial\"",
          explanation:
            "\"Troca pendente\" significa que o titular ainda está com a senha temporária que você entregou e nunca entrou para trocá-la. É o sinal mais direto de cliente que recebeu o acesso e ainda não começou a usar.",
        },
        {
          label: "Coluna \"Implantação\"",
          explanation:
            "O selo indica em que etapa da ativação a propriedade parou (titular, plano, repasse, Asaas, Channel Manager, catálogo, quartos, tarifas). \"Pronta para ativar\" quer dizer que a checklist acabou e falta só o go-live; \"Ativo\" é operação normal. O detalhe etapa a etapa fica na tela Implantações.",
        },
        {
          label: "Menu de ações da linha",
          domKey: "admin.tenants.row_actions",
          visual: { icon: MoreVertical, text: "Mais ações", tone: "outline" },
          explanation:
            "Reúne as quatro ações da propriedade: Gerenciar (plano e dados), Conta Asaas (subconta de pagamento), Entrar na propriedade e Ativar/Suspender. Abre por linha e fecha ao clicar fora ou apertar Esc.",
        },
        {
          label: "Gerenciar / Conta Asaas (dentro do menu)",
          visual: { icon: Settings2, text: "Gerenciar", tone: "outline" },
          explanation:
            "\"Gerenciar\" abre o diálogo do titular e do plano da propriedade. \"Conta Asaas\" abre a subconta de pagamento — o ícone fica amarelo quando o cadastro da subconta está inconsistente e verde quando está configurado, então dá para ver o problema sem abrir.",
        },
        {
          label: "Entrar na propriedade (dentro do menu)",
          visual: { icon: LogIn, text: "Entrar na propriedade", tone: "outline" },
          explanation:
            "Assume a identidade do titular e leva você para a Visão Geral do hotel — você passa a ver e a poder fazer exatamente o que ele faz. Fica registrado na auditoria com o motivo \"Suporte operacional via Central de Propriedades\" e o nome do hotel. Fica desabilitado enquanto a propriedade não tiver titular cadastrado.",
        },
        {
          label: "Ativar / Suspender propriedade (dentro do menu)",
          visual: { icon: ToggleRight, text: "Suspender propriedade", tone: "danger" },
          explanation:
            "Marca a propriedade como suspensa sem apagar nada — os dados continuam lá e reativar é um clique. Este marcador sozinho não é o mesmo que corte de acesso por inadimplência: o bloqueio comercial vem do status da assinatura (pagamento atrasado ou cancelada), que aparece na coluna Plano e cobrança.",
        },
      ],
    },
  ],
  "/administracao/implantacoes": [
    {
      title: "O que esta tela faz",
      description:
        "É a esteira de ativação: um card por propriedade mostrando exatamente o que falta para ela sair do onboarding e virar operação. Enquanto a checklist não fecha, o hotel não consegue operar de verdade — não tem quarto, tarifa ou canal de venda configurados. Use esta tela para saber onde cada cliente empacou, em vez de perguntar para ele.",
      items: [
        {
          label: "Cards de resumo do topo",
          domKey: "admin.onboarding.summary",
          explanation:
            "Propriedades no total, quantas já estão em operação, quantas estão prontas para ativar e quantas têm impedimento. \"Com impedimento\" é a fila que precisa de você: são etapas travadas esperando outra etapa terminar.",
        },
        {
          label: "Ordem dos cards",
          explanation:
            "As propriedades já ativas caem para o fim da lista e o resto vem ordenado por percentual concluído — quem está mais perto de ativar aparece primeiro.",
        },
        {
          label: "Barra de progresso",
          domKey: "admin.onboarding.progress",
          explanation:
            "Etapas concluídas sobre o total. A barra fica amarela quando existe impedimento. As etapas têm dependência entre si numa ordem fixa: schema, titular, plano, política de repasse, conta Asaas, Channel Manager, catálogo, quartos físicos e tarifas — Channel Manager e quartos físicos dependem do plano, e tarifas dependem dos quartos. Enquanto a etapa de baixo não fecha, a de cima fica bloqueada e o botão dela vira \"Conclua a etapa anterior\".",
        },
        {
          label: "Botão da etapa",
          domKey: "admin.onboarding.step_action",
          visual: { text: "Resolver etapa", tone: "outline" },
          explanation:
            "Cada etapa leva à ação certa em vez de você caçar a tela: titular e plano abrem o diálogo de gestão do proprietário; conta Asaas abre a subconta; política de repasse leva para Configurações da plataforma; Channel Manager e catálogo reenfileiram o provisionamento (botão \"Executar agora\"); quartos físicos e tarifas trocam o contexto para aquela propriedade e navegam para Configurações e Tarifas dela.",
        },
        {
          label: "Motivo do impedimento",
          explanation:
            "Quando uma etapa está travada, o texto em amarelo diz qual etapa anterior a está segurando. Não adianta tentar resolver a de cima — resolva a citada e a seguinte destrava sozinha.",
        },
        {
          label: "Atalhos no card (Gerenciar, Titular, Plano, Asaas)",
          domKey: "admin.onboarding.card_actions",
          visual: { icon: UserRound, text: "Titular", tone: "outline" },
          explanation:
            "As mesmas ações das etapas, disponíveis a qualquer momento — servem para revisar ou corrigir algo que já foi feito, não só para concluir o que falta.",
        },
        {
          label: "Reprocessar Channel Manager",
          visual: { text: "Reprocessar Channel Manager", tone: "primary" },
          explanation:
            "Aparece na faixa do rodapé do card quando a propriedade está parada esperando o Beds24 provisionar ou importar o catálogo. Só reenfileira o trabalho — se a falha for do lado do Beds24, reenfileirar de novo não resolve; confira as falhas na tela de Filas.",
        },
        {
          label: "Atualizar checklist",
          domKey: "admin.onboarding.refresh",
          visual: { icon: RefreshCw, text: "Atualizar", tone: "outline" },
          explanation:
            "As etapas são verificadas no servidor no momento da consulta (existe schema? existe titular? existem tarifas?). Depois de disparar um provisionamento, atualize aqui para ver se a etapa fechou.",
        },
      ],
    },
  ],
  "/administracao/portais-dos-hospedes": [
    {
      title: "O que esta tela faz",
      description:
        "O portal do hóspede é a página que o próprio hóspede acessa para fazer pré-check-in e avisar que chegou. Esta é a visão Allmight dela: as reservas com portal ativo de TODAS as propriedades da plataforma, numa lista só. Serve para atender um hóspede que ligou reclamando do portal sem precisar entrar na conta do hotel.",
      items: [
        {
          label: "Buscar por hóspede ou código da reserva",
          explanation:
            "A busca só acontece quando você aperta Enter ou clica em Buscar — a lista não filtra enquanto você digita. Sem termo, ela traz as reservas com portal de todas as propriedades.",
        },
        {
          label: "Selo \"Chegou · aguarda recepção\"",
          domKey: "admin.guest_portals.arrival_badge",
          explanation:
            "O hóspede apertou \"cheguei\" no portal e a recepção ainda não confirmou. É o único selo que representa alguém esperando atendimento agora — tem prioridade sobre o de pré-check-in.",
        },
        {
          label: "Selo \"Pré-check-in concluído\"",
          domKey: "admin.guest_portals.precheckin_badge",
          explanation:
            "O hóspede já preencheu os dados dele pelo portal antes de chegar. A recepção pode fazer o check-in mais rápido porque a ficha já veio preenchida.",
        },
        {
          label: "Entrar como Allmight",
          domKey: "admin.guest_portals.master_access",
          visual: { icon: ShieldCheck, text: "Entrar como Allmight", tone: "outline" },
          explanation:
            "Abre numa aba nova exatamente a tela que aquele hóspede está vendo, com uma sessão administrativa criada na hora — é como você reproduz o problema que ele relatou. A ação fica registrada na auditoria. É diferente do link \"Abrir portal\" que a própria propriedade usa na versão dela desta tela: aquele é o link direto do hóspede e não gera sessão administrativa.",
        },
        {
          label: "\"Portal indisponível\"",
          explanation:
            "Aquela reserva não tem link de portal gerado. Não é falha de permissão sua — não há o que abrir para o hóspede também.",
        },
      ],
    },
  ],
  "/administracao/notas-fiscais": [
    {
      title: "O que esta tela faz",
      description:
        "Emissão de NFS-e da CONTA-MÃE InnSight: as notas do que a InnSight cobra dos hoteleiros (mensalidade, comissão, serviços). Não é aqui que se emite nota de hospedagem — a nota que o hotel dá ao hóspede sai do módulo fiscal da propriedade, com o CNPJ e a prefeitura dele, em tela separada.",
      items: [
        {
          label: "Painel de configuração fiscal (no topo)",
          explanation:
            "Mostra se o CNPJ da conta-mãe já está habilitado a emitir: \"Configuração ativa\" ou \"Configuração pendente\". Enquanto estiver pendente, o formulário abaixo não deixa agendar nota nenhuma. Esta credencial fiscal é exclusiva da conta-mãe e nunca é usada para notas de hospedagem.",
          fact: (ctx) =>
            ctx.payments.asaas_environment === "sandbox"
              ? "o Asaas está em homologação (sandbox) — notas agendadas aqui não têm validade fiscal."
              : "o Asaas está em produção — as notas agendadas aqui são emitidas de verdade.",
        },
        {
          label: "Cliente hoteleiro",
          domKey: "admin.fiscal.customer",
          explanation:
            "Lista só quem já está cadastrado como cliente na conta-mãe do Asaas, com documento. Se o hotel não aparece na lista, o cadastro dele precisa existir antes — não dá para digitar um cliente novo por aqui.",
        },
        {
          label: "Serviço municipal",
          domKey: "admin.fiscal.municipal_service",
          explanation:
            "Escolher um serviço da lista pré-cadastrada preenche a alíquota de ISS sozinho e esconde os campos de código e nome, porque eles já vêm do cadastro. Deixando em \"Informar código manualmente\", você assume a responsabilidade de digitar o código e o nome corretos — é o caminho para prefeitura cujo serviço ainda não foi cadastrado.",
        },
        {
          label: "Código municipal e Nome do serviço",
          domKey: "admin.fiscal.manual_service_code",
          explanation:
            "Só aparecem no modo manual. São o que a prefeitura usa para classificar a nota — código errado costuma resultar em rejeição na emissão, não em erro aqui na tela.",
        },
        {
          label: "Agendar NFS-e",
          domKey: "admin.fiscal.schedule",
          visual: { icon: Send, text: "Agendar NFS-e", tone: "primary" },
          explanation:
            "Agenda a nota para a data de emissão informada — não emite na hora. Fica desabilitado enquanto o módulo fiscal da conta-mãe estiver pendente no painel acima.",
        },
        {
          label: "Emitir agora",
          domKey: "admin.fiscal.issue_now",
          explanation:
            "Força a emissão imediata de uma nota que estava esperando a data programada. Só fica habilitado em notas na situação \"SCHEDULED\" (agendada) — em nota já emitida, cancelada ou com erro o botão continua apagado.",
        },
      ],
    },
  ],
  "/administracao/whatsapp": [
    {
      title: "O que esta tela faz",
      description:
        "Configura a instância padrão de WhatsApp da plataforma (Evolution API) — a que é usada quando não há uma propriedade específica no contexto: códigos de acesso do portal do hóspede, avisos operacionais e atendimento do próprio InnSight. Cada hotel pode ter a instância dele, configurada em Configurações > Integrações da propriedade; o servidor Evolution é o mesmo, o que muda é a instância nomeada dentro dele.",
      items: [
        {
          label: "Saúde do servidor Evolution",
          domKey: "admin.whatsapp.server_health",
          explanation:
            "Endpoint, versão, latência e quantas instâncias estão conectadas. Se o servidor está indisponível aqui, nenhum hotel envia WhatsApp — o problema é da infraestrutura, não da configuração de cada propriedade. Esta faixa se atualiza sozinha a cada 15 segundos enquanto a tela estiver aberta e ociosa.",
        },
        {
          label: "Lista de instâncias",
          explanation:
            "Todas as instâncias que o servidor Evolution conhece, com o estado de cada uma. \"Sincronizando\" é uma instância que acabou de parear e ainda está baixando o histórico de conversas — nesse estado, gerar QR Code de novo só atrapalha; espere e use \"Consultar estado\".",
        },
        {
          label: "Nome da instância",
          domKey: "admin.whatsapp.instance",
          explanation:
            "O identificador da conexão dentro do servidor Evolution. É por ele que o sistema sabe qual número usar — trocar o nome aponta a plataforma para outra conexão, e o pareamento da nova precisa ser feito do zero.",
          fact: (ctx) => {
            const nome = ctx.whatsapp.instance.trim();
            if (nome === "") {
              return ctx.whatsapp.enabled
                ? "o envio está habilitado, mas nenhum nome de instância está salvo — não há conexão para usar."
                : "nenhum nome de instância salvo e o envio está desligado.";
            }
            return ctx.whatsapp.enabled
              ? `instância salva: "${nome}", com envio habilitado.`
              : `instância salva: "${nome}", mas o envio está desligado — nada sai por ela.`;
          },
        },
        {
          label: "Tempo limite (segundos)",
          domKey: "admin.whatsapp.timeout",
          explanation:
            "Quanto o sistema espera o servidor Evolution responder antes de desistir de uma chamada. Aceita de 3 a 30 segundos. Valor muito baixo derruba envio em servidor lento; muito alto trava a tela do usuário esperando.",
        },
        {
          label: "Habilitar WhatsApp empresarial",
          domKey: "admin.whatsapp.enabled",
          explanation:
            "Chave geral do envio por esta instância. Com ela desligada, o teste de entrega é recusado pelo servidor mesmo com tudo pareado. Habilitar sozinho também não basta: sem o pareamento concluído não há sessão de WhatsApp para enviar.",
        },
        {
          label: "URL e chave da Evolution API",
          explanation:
            "Não são configuradas nesta tela — são fixas na infraestrutura da plataforma. Se o endereço não pertencer à infraestrutura aprovada, o sistema recusa a conexão em vez de tentar falar com um servidor desconhecido.",
        },
        {
          label: "Salvar configuração",
          domKey: "admin.whatsapp.save",
          visual: { icon: Save, text: "Salvar configuração", tone: "primary" },
          explanation:
            "Grava nome da instância, tempo limite e a chave de habilitação. É o que precisa acontecer antes de gerar o QR Code: o pareamento é feito contra a instância salva, não contra o que está digitado na tela.",
        },
        {
          label: "Consultar estado",
          domKey: "admin.whatsapp.status",
          visual: { icon: RefreshCw, text: "Consultar estado", tone: "outline" },
          explanation:
            "Pergunta ao servidor Evolution como está a conexão desta instância agora. É o jeito certo de acompanhar um pareamento em andamento, em vez de ficar gerando QR Code.",
        },
        {
          label: "Gerar QR Code",
          domKey: "admin.whatsapp.qr",
          visual: { icon: QrCode, text: "Gerar QR Code", tone: "outline" },
          explanation:
            "Puxa o código para você ler no aplicativo do WhatsApp do número da empresa e concluir o pareamento. Exige a instância já salva, mas NÃO exige que o envio esteja habilitado — dá para parear primeiro e ligar o envio depois. Se a Evolution não devolver código, normalmente é porque a instância já está conectada ou ainda está sincronizando.",
        },
        {
          label: "Teste de entrega",
          domKey: "admin.whatsapp.test",
          visual: { icon: Send, text: "Enviar mensagem de teste", tone: "primary" },
          explanation:
            "Manda uma mensagem para um número que você informar, sem passar por nenhum fluxo real do sistema — é a prova de que a instância está de fato entregando. Exige a chave \"Habilitar WhatsApp empresarial\" ligada e a instância configurada; o envio é abortado se passar de 15 segundos.",
        },
      ],
    },
  ],
  "/administracao/sessoes": [
    {
      title: "O que esta tela faz",
      description:
        "Todo login gera um token de acesso (JWT) que continua valendo até expirar, mesmo que a pessoa feche o navegador. Esta tela lista esses tokens em toda a plataforma e permite derrubá-los. É a ferramenta para desligar o acesso de alguém agora — desligar o usuário não invalida o token que ele já tem na mão; revogar a sessão, sim.",
      items: [
        {
          label: "Filtrar por hotel",
          domKey: "admin.sessions.tenant_filter",
          explanation:
            "\"Todos os hotéis (Geral)\" mostra a plataforma inteira, inclusive sessões administrativas sem propriedade (aparecem como \"Público / administração\"). Escolher um hotel específico é também o que libera a revogação em lote.",
        },
        {
          label: "Apenas sessões não expiradas e ativas",
          domKey: "admin.sessions.active_only",
          explanation:
            "Vem marcado. Desmarcar traz também as sessões já expiradas e as revogadas antes — é a visão de auditoria, para responder \"quem estava logado naquele dia\", não a visão de quem está dentro agora.",
        },
        {
          label: "Revogar sessão da linha",
          domKey: "admin.sessions.revoke",
          visual: { icon: Ban, text: "Revogar", tone: "danger" },
          explanation:
            "Derruba aquele token imediatamente: a pessoa é obrigada a logar de novo na próxima requisição. Só aparece em sessão que ainda está válida — em linha já revogada ou expirada, o lugar do botão mostra a situação em vez da ação.",
        },
        {
          label: "IP e dispositivo",
          explanation:
            "IP de origem e navegador que abriram a sessão. É por aqui que você diferencia o acesso legítimo do titular de um acesso que não deveria existir, antes de revogar.",
        },
        {
          label: "Revogar tudo do hotel",
          domKey: "admin.sessions.bulk_revoke",
          visual: { icon: Ban, text: "Revogar tudo do hotel", tone: "danger" },
          explanation:
            "Só existe com uma propriedade específica escolhida no filtro — some quando está em \"Todos os hotéis\". Desconecta TODOS os usuários daquele hotel de uma vez, inclusive quem está no meio de um check-in. Pede confirmação antes; é a resposta a vazamento de credencial, não rotina.",
        },
      ],
    },
  ],
  "/administracao/auditoria": [
    {
      title: "O que esta tela faz",
      description:
        "O histórico do que foi feito na plataforma: quem fez, em qual hotel, quando e o que exatamente mudou. É a fonte para responder \"quem alterou isso?\" — inclusive as ações do próprio Allmight, como entrar na conta de um cliente ou abrir o portal de um hóspede.",
      items: [
        {
          label: "Selo de risco (Baixo / Médio / Alto)",
          domKey: "admin.audit.risk",
          explanation:
            "Sensibilidade da ação, classificada pelo backend conforme o tipo de evento — não é algo que o operador escolhe. Serve para varrer a lista procurando o que merece atenção sem ler evento por evento.",
        },
        {
          label: "Categoria / Módulo",
          domKey: "admin.audit.category_filter",
          explanation:
            "Separa Administrativo (ações do Allmight), Autenticação (login, troca de senha, impersonação), Faturamento (cobrança e assinatura) e Geral / Operações (o dia a dia do hotel).",
        },
        {
          label: "Data inicial e Data final",
          domKey: "admin.audit.date_range",
          explanation:
            "Recortam o período no servidor. Sem elas a consulta traz a plataforma inteira paginada, o que é lento e difícil de ler — em investigação, comece pelo intervalo do incidente.",
        },
        {
          label: "Coluna \"Hotel\"",
          explanation:
            "\"Sistema Geral\" quer dizer que a ação não pertence a nenhuma propriedade — é ação de plataforma. Qualquer outro valor é a propriedade em que o evento aconteceu.",
        },
        {
          label: "Ver detalhes",
          domKey: "admin.audit.details",
          visual: { icon: Eye, text: "Ver detalhes", tone: "outline" },
          explanation:
            "Abre o registro completo, com os metadados originais em JSON — é onde estão os valores antigos e novos do que mudou. A linha da tabela resume o evento; o que sustenta uma investigação está aqui dentro.",
        },
      ],
    },
  ],
  "/administracao/filas": [
    {
      title: "O que esta tela faz",
      description:
        "Boa parte do trabalho pesado do sistema roda em segundo plano (provisionar Channel Manager, sincronizar canal, enviar mensagem, gerar relatório). Quando uma dessas tarefas falha, ela cai aqui. Esta lista é o motivo mais comum de \"eu mandei e não aconteceu nada\" — se o hotel diz que algo não processou, é aqui que se confirma. Tela restrita a super administradores.",
      items: [
        {
          label: "Cartão \"Total de Falhas\" e cartões por fila",
          domKey: "admin.queue.stats",
          explanation:
            "O total de tarefas com falha e o recorte das três primeiras filas com ocorrência. Muitas falhas concentradas numa única fila normalmente apontam para um serviço externo fora do ar, não para um bug espalhado.",
        },
        {
          label: "Buscar e Filtrar por fila",
          explanation:
            "A busca varre a classe do job e o texto da exceção — dá para procurar pelo nome do erro. O filtro de fila aceita o nome exato (ex.: default).",
        },
        {
          label: "Re-enfileirar job",
          domKey: "admin.queue.retry_job",
          visual: { icon: RotateCcw, text: "Re-enfileirar job", tone: "outline" },
          explanation:
            "Executa aquela tarefa de novo, do zero, com os mesmos dados. Faz sentido quando a causa foi passageira (serviço externo fora do ar por alguns minutos). Se a causa for dado inválido, ela vai falhar de novo igual.",
        },
        {
          label: "Remover job",
          domKey: "admin.queue.delete_job",
          visual: { icon: Trash2, text: "Remover job", tone: "danger" },
          explanation:
            "Tira a tarefa da lista de falhas sem tentar reprocessar. Use quando reexecutar não faz mais sentido (a reserva já foi cancelada, o dado já foi corrigido na mão) — o trabalho pendente é descartado, não concluído.",
        },
        {
          label: "Ver detalhes do job",
          domKey: "admin.queue.expand_job",
          visual: { icon: ChevronDown, text: "Ver detalhes", tone: "outline" },
          explanation:
            "Expande as primeiras linhas da exceção que derrubou a tarefa. É um recorte para diagnóstico rápido, não o stack trace completo — o traço inteiro fica no log do servidor.",
        },
        {
          label: "Retry All",
          domKey: "admin.queue.retry_all",
          visual: { icon: RotateCcw, text: "Retry All", tone: "outline" },
          explanation:
            "Re-enfileira todas as tarefas com falha de uma vez. É o que se faz depois que um serviço externo voltou ao ar. Fica desabilitado quando não há falha nenhuma.",
        },
        {
          label: "Flush",
          domKey: "admin.queue.flush",
          visual: { icon: Trash2, text: "Flush", tone: "danger" },
          explanation:
            "Apaga TODAS as tarefas com falha sem reprocessar nenhuma. Não tem desfazer e o trabalho perdido não volta — pede confirmação com o total antes. Só use depois de já ter olhado o que está na lista.",
        },
      ],
    },
  ],
  "/administracao/permissoes": [
    {
      title: "O que esta tela faz",
      description:
        "Define o que cada perfil de acesso (recepção, governança, gerente...) pode fazer, permissão por permissão, agrupada por módulo. Vale para a plataforma inteira: tirar uma permissão de \"Recepção\" tira de todos os recepcionistas de todos os hotéis. Tela restrita a super administradores.",
      items: [
        {
          label: "Célula de permissão (✓ / ✗)",
          domKey: "admin.permissions.cell",
          explanation:
            "Clicar alterna só na tela — é rascunho. Nada chega ao servidor até você salvar, então dá para revisar a grade inteira antes de aplicar. Sair da tela sem salvar descarta tudo sem aviso.",
        },
        {
          label: "Cabeçalho do módulo",
          explanation:
            "Recolhe e expande o bloco de permissões daquele módulo, com a contagem de permissões ao lado. Todos começam abertos; recolher os que não interessam ajuda a comparar perfis numa grade grande.",
        },
        {
          label: "Coluna \"Dono do sistema\"",
          explanation:
            "É o super_admin, e vem travada de propósito: as células ficam apagadas e o salvamento nem envia esse perfil. É a trava que impede alguém remover a própria permissão de administrar e ficar sem como voltar atrás.",
        },
        {
          label: "Novo perfil",
          domKey: "admin.permissions.new_role",
          visual: { icon: Plus, text: "Novo perfil", tone: "outline" },
          explanation:
            "Abre um campo para o nome técnico do perfil — só letras minúsculas, números e underline (o campo remove o resto conforme você digita). O perfil nasce sem nenhuma permissão e vira uma coluna nova na grade, que você marca em seguida.",
        },
        {
          label: "Resetar",
          domKey: "admin.permissions.reset",
          visual: { icon: RefreshCw, text: "Resetar", tone: "outline" },
          explanation:
            "Joga fora todo o rascunho e recarrega a matriz do servidor. É a saída quando você se perdeu no meio de muitas marcações e prefere recomeçar do estado real.",
        },
        {
          label: "Salvar Alterações",
          domKey: "admin.permissions.save",
          visual: { icon: Save, text: "Salvar Alterações", tone: "primary" },
          explanation:
            "Envia a grade inteira de uma vez: para cada perfil, o conjunto de permissões passa a ser exatamente o que está marcado na tela. Só fica habilitado quando existe rascunho pendente. Se o servidor recusar, a tela volta a mostrar os dados reais em vez de fingir que salvou.",
        },
        {
          label: "Banner \"Você tem alterações não salvas\"",
          domKey: "admin.permissions.unsaved_banner",
          explanation:
            "Fica fixo no rodapé enquanto houver rascunho, com atalho para salvar ou descartar. É o lembrete de que a grade na tela ainda não é a que está valendo para os usuários.",
        },
      ],
    },
  ],
  "/administracao/modelos-de-governanca": [
    {
      title: "O que esta tela faz",
      description:
        "Monta as checklists de inspeção que a governança usa para liberar um quarto depois da limpeza — são os mesmos modelos que aparecem no ícone de inspeção da tela de Governança. O resultado da inspeção não é decorativo: quando ela passa, o sistema marca o quarto como Limpo sozinho e ele volta a poder receber hóspede.",
      items: [
        {
          label: "Cards do topo",
          explanation:
            "Inspeções pendentes, aprovadas e reprovadas, mais quartos sujos e em limpeza no momento. Servem para ver se o modelo que você está montando está reprovando quarto demais na prática.",
        },
        {
          label: "Lista de Templates",
          explanation:
            "Modelos ativos primeiro, depois os inativos, em ordem alfabética, com a contagem de itens. Clicar em um traz ele para edição no painel da direita.",
        },
        {
          label: "Novo",
          domKey: "admin.housekeeping_templates.new",
          visual: { icon: Plus, text: "Novo", tone: "outline" },
          explanation:
            "Abre um formulário em branco com um item vazio. Atenção: o que estiver em edição no painel da direita e não tiver sido salvo é perdido — não existe salvamento automático ao trocar de modelo.",
        },
        {
          label: "Ativo",
          domKey: "admin.housekeeping_templates.active",
          explanation:
            "Modelo inativo continua guardado mas sai da lista de checklists que a governança pode rodar. É como você aposenta um modelo sem perder o histórico das inspeções que já foram feitas com ele.",
        },
        {
          label: "Área e Pergunta do item",
          explanation:
            "Área é o agrupamento (Quarto, Banheiro...) e a pergunta é o que o inspetor responde. Os dois são obrigatórios em todos os itens — salvar com qualquer um em branco é recusado. A nota final também é calculada por área, então a área não é só rótulo: é o que permite ver onde a limpeza falha com mais frequência.",
        },
        {
          label: "Peso",
          domKey: "admin.housekeeping_templates.weight",
          explanation:
            "Quanto aquele item vale na nota final, de 1 a 100. A nota é a média ponderada de todos os itens, e a inspeção só é aprovada com nota igual ou maior que 90. Dar peso alto a um item é o jeito de fazer ele quase sozinho reprovar o quarto.",
        },
        {
          label: "Obrig. (obrigatório)",
          domKey: "admin.housekeeping_templates.mandatory",
          explanation:
            "Item obrigatório não pode ficar sem resposta: a inspeção nem é aceita, o sistema devolve o nome das perguntas que faltaram. E se um obrigatório for respondido como não conforme, a inspeção reprova mesmo que a nota geral tenha passado de 90 — é o veto para o que não se negocia.",
        },
        {
          label: "Foto",
          domKey: "admin.housekeeping_templates.photo",
          explanation:
            "Exige que o inspetor anexe uma foto naquele item. Sem a foto a inspeção inteira é recusada no envio, não só aquele item. Use com parcimônia: cada foto obrigatória é tempo a mais da camareira em cada quarto.",
        },
        {
          label: "Item (adicionar)",
          domKey: "admin.housekeeping_templates.add_item",
          visual: { icon: Plus, text: "Item", tone: "outline" },
          explanation:
            "Acrescenta uma linha na checklist. O modelo precisa ter pelo menos um item — a lixeira do último item fica desabilitada.",
        },
        {
          label: "Salvar",
          domKey: "admin.housekeeping_templates.save",
          visual: { icon: Save, text: "Salvar", tone: "primary" },
          explanation:
            "Grava o modelo e limpa o painel para um novo. Salvar um modelo já usado altera as inspeções futuras; as inspeções já concluídas mantêm os itens e a nota que tinham quando foram feitas.",
        },
        {
          label: "Remover",
          domKey: "admin.housekeeping_templates.remove",
          visual: { icon: Trash2, text: "Remover", tone: "outline" },
          explanation:
            "Só aparece com um modelo salvo aberto. Remove ou inativa o modelo (o sistema inativa quando ele já tem histórico de inspeção, para não apagar o passado). Pede confirmação com o nome antes.",
        },
      ],
    },
  ],
  "/administracao/whatsapp/mensagens": [
    {
      title: "O que esta tela faz",
      description:
        "É a central de atendimento do Allmight para acompanhar as mensagens automáticas e responder manualmente pelos WhatsApps conectados. Você consulta qualquer propriedade sem entrar na conta dela; a sessão escolhida define de qual instância Evolution a resposta será enviada.",
      items: [
        {
          label: "Sessão de auditoria",
          domKey: "admin.whatsapp.messages.session",
          explanation:
            "Escolha uma propriedade e uma instância para carregar contatos e conversas daquele WhatsApp. A opção de todas as propriedades é somente consulta: para responder, selecione uma sessão específica e confirme que a instância está conectada.",
        },
        {
          label: "Buscar conversa ou telefone",
          domKey: "admin.whatsapp.messages.search",
          explanation:
            "Filtra o histórico pelo telefone completo ou por parte dele. O filtro também seleciona automaticamente a primeira conversa encontrada, facilitando localizar um hóspede sem percorrer a lista inteira.",
        },
        {
          label: "Atualizar",
          domKey: "admin.whatsapp.messages.refresh",
          visual: { icon: RefreshCw, text: "Atualizar", tone: "outline" },
          explanation:
            "Reconsulta sessões, contatos e mensagens. A lista de mensagens também é atualizada automaticamente a cada 15 segundos enquanto a tela está aberta.",
        },
        {
          label: "Contatos salvos",
          domKey: "admin.whatsapp.messages.contacts",
          explanation:
            "Mostra os contatos retornados pela agenda da instância e, quando houver vínculo, o hóspede e a reserva reconhecidos pelo telefone. O nome vem do WhatsApp e não é editado pelo Innsight.",
        },
        {
          label: "Lista de conversas",
          domKey: "admin.whatsapp.messages.conversations",
          explanation:
            "Agrupa todas as mensagens do mesmo telefone em um único chat, independentemente de terem sido automáticas, recebidas ou enviadas manualmente. O contador informa quantas mensagens existem naquele histórico.",
        },
        {
          label: "Reenviar última mensagem",
          domKey: "admin.whatsapp.messages.resend",
          visual: { icon: RotateCcw, text: "Reenviar", tone: "outline" },
          explanation:
            "Recoloca a última mensagem enviada na fila. Use quando a Evolution retornar falha transitória; o sistema registra uma nova tentativa e mantém a mensagem original no histórico.",
        },
        {
          label: "Campo de resposta",
          domKey: "admin.whatsapp.messages.composer",
          explanation:
            "Digite a resposta que será enviada pela instância da propriedade selecionada. O campo fica bloqueado sem uma sessão específica para impedir que uma mensagem saia pelo WhatsApp errado.",
        },
        {
          label: "Enviar",
          domKey: "admin.whatsapp.messages.send",
          visual: { icon: Send, text: "Enviar", tone: "primary" },
          explanation:
            "Coloca a resposta na fila Redis e atualiza o chat quando o servidor aceitar o envio. Em caso de falha, a mensagem permanece registrada com o motivo para permitir uma nova tentativa sem duplicar silenciosamente o atendimento.",
        },
      ],
    },
  ],
};
